
// ✅ src/pages/VehicleV2/JobCardEntryDetailsReport_AllPrint.jsx
// Single job card print
// Logo: src/assets/infraLogo.jpg

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import companyLogo from "../../assets/infraLogo.jpg";

const API_BASE = "https://nissi-india.com/api/jobCardEntryReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const display = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "—" : v;

const ls = (...keys) => {
  if (typeof window === "undefined") return "";
  for (const k of keys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v !== null && v !== undefined && String(v).trim() !== "") return String(v).trim();
  }
  return "";
};

export default function JobCardEntryDetailsReport_Print() {
  const navigate = useNavigate();
  const { search } = useLocation();

  const jobcardEntryId = useMemo(() => {
    const s = (search || "").replace("?", "").trim();
    if (!s) return 0;
    if (s.includes("=")) {
      const qs = new URLSearchParams(search);
      return toInt(qs.get("id") || qs.get("jobcardEntryDetId") || qs.get("jobcardEntryId"));
    }
    return toInt(s);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState(null);

  const finYear = ls("finYear", "FinYear");
  const finYearId = ls("finYearId", "finyearid", "x-finyearid");
  const branchType = ls("branchType", "branchtype", "BranchType") || "HO";
  const branchId = ls("branchId", "Branchid", "branchid", "loginBranchId") || "0";

  const mapRow = (r = {}) => ({
    vehicleMainType: r.VehicleType ?? r.fld_vehicletypename ?? "",
    jobCardNo: r.fld_jobcardno ?? "",
    entryDate: r.fld_entrydate1 ?? r.fld_entrydate ?? "",
    vehicleName: r.fld_vehicle_name ?? r.fld_Vehiclename ?? "",
    vehicleRegNo: r.fld_vehicle_regno ?? "",
    location: r.fld_worklocationname ?? r.fld_branchname ?? "",
    hrKm: r.fld_hrkm ?? r.fld_workinghrs ?? r.fld_workingkm ?? r.fld_hrs ?? "",
    status: r.fld_status ?? "",
    complaint: r.fld_natureofcomplaint ?? "",
    attendedBy: r.fld_complaintattendedby ?? "",
    inspectedBy: r.fld_inspectedby ?? "",
    remedy: r.fld_remedy ?? "",
    fromDate: r.fld_maintainfromdate1 ?? r.fld_fromdate ?? "",
    inTime: r.fld_maintainintime ?? r.fld_intime ?? "",
    toDate: r.fld_maintaintodate1 ?? r.fld_todate ?? "",
    outTime: r.fld_maintainouttime ?? r.fld_outtime ?? "",
    hrs: r.fld_hours ?? r.fld_maintainhrs ?? r.fld_hrs ?? "",
    sparesDetails: r.fld_sparedetails ?? "",
    maintenanceBy: r.fld_maintenanceby ?? "",
  });

  const fetchOne = async () => {
    if (!jobcardEntryId) return;

    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/print/${jobcardEntryId}`, {
        params: {
          branchType,
          branchId,
          loginBranchId: branchId,
        },
        headers: {
          "x-branch": String(branchType),
          "x-branchid": String(branchId),
          "x-finyear": String(finYear),
          "x-finyearid": String(finYearId),
        },
      });

      if (!res.data?.success) throw new Error(res.data?.message || "Failed");
      setRow(mapRow(res.data.row));
    } catch (e) {
      setRow(null);
      alert(e?.response?.data?.message || e.message || "Failed to load print data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobcardEntryId]);

  const handlePrint = () => window.print();

  const currentDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const currentDateTime = useMemo(
    () =>
      new Date().toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  return (
    <div className="report-page min-h-screen bg-white">
      <style jsx global>{`
        :root {
          --navy: #163a5f;
          --navy-deep: #0f2a45;
          --gold: #e8a317;
          --slate: #5b6b82;
          --line: #c5ced8;
          --ink: #1c2430;
        }
        .report-page { color: var(--ink); font-family: "Segoe UI", Calibri, Arial, sans-serif; }
        .letterhead { border: 1.5px solid var(--navy); overflow: hidden; background: #fff; }
        .letterhead-topbar { height: 6px; background: linear-gradient(90deg, #0f2a45 0%, #163a5f 55%, #e8a317 100%); }
        .letterhead-body { display: flex; align-items: center; gap: 16px; padding: 10px 14px 8px; }
        .logo-wrap { flex: 0 0 auto; width: 82px; height: 70px; display: flex; align-items: center; justify-content: center; }
        .logo-wrap img { max-width: 82px; max-height: 70px; object-fit: contain; display: block; }
        .company-block { flex: 1 1 auto; text-align: center; min-width: 0; }
        .brand-name { font-size: 20px; letter-spacing: 1.4px; font-weight: 800; color: var(--navy); line-height: 1.1; margin: 0; }
        .legal-name { margin: 2px 0 0; font-size: 12px; font-weight: 600; color: #2c3e50; }
        .addr-line { margin: 3px 0 0; font-size: 10.5px; color: #4b5563; line-height: 1.4; }
        .meta-block { flex: 0 0 150px; text-align: right; font-size: 10.5px; color: #334155; line-height: 1.35; }
        .meta-label { display: block; font-size: 8.5px; letter-spacing: 0.7px; text-transform: uppercase; color: var(--slate); font-weight: 700; }
        .meta-value { display: block; font-weight: 700; color: var(--navy); }
        .letterhead-bottombar { height: 3px; background: var(--gold); }
        .doc-title-wrap { margin: 12px 0 8px; text-align: center; }
        .doc-title { margin: 0; font-size: 16px; font-weight: 800; letter-spacing: 1.2px; color: var(--navy); }
        .doc-sub { margin: 2px 0 0; font-size: 10.5px; color: var(--slate); }
        .title-rule { width: 80px; height: 3px; background: var(--gold); margin: 6px auto 0; }
        .id-strip { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid var(--line); background: #f6f8fb; margin-bottom: 10px; }
        .id-cell { padding: 6px 10px; border-right: 1px solid var(--line); }
        .id-cell:last-child { border-right: none; }
        .id-k { display: block; font-size: 8.5px; font-weight: 700; letter-spacing: 0.6px; text-transform: uppercase; color: var(--slate); }
        .id-v { display: block; margin-top: 1px; font-size: 12px; font-weight: 700; color: var(--navy); }
        .rpt-table { width: 100%; border-collapse: collapse; }
        .rpt-table th { background: var(--navy); color: #fff; font-size: 7px; font-weight: 700; padding: 5px 2px; border: 1px solid var(--navy-deep); text-align: center; line-height: 1.15; }
        .rpt-table td { border: 1px solid #d0d7e0; padding: 4px 2px; font-size: 7.5px; color: #1f2937; text-align: center; vertical-align: top; }
        .rpt-table td.left { text-align: left; }
        .sign-row { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 16px; gap: 24px; }
        .gen-by { font-size: 10.5px; color: #475569; line-height: 1.45; }
        .sign-box { width: 190px; text-align: center; }
        .sign-line { border-top: 1px solid #64748b; padding-top: 5px; font-size: 11px; font-weight: 700; color: var(--navy); }
        .sign-hint { font-size: 9.5px; color: #64748b; margin-top: 2px; }
        .rpt-footer { margin-top: 12px; border-top: 1px solid var(--line); padding-top: 7px; display: flex; justify-content: space-between; font-size: 9.5px; color: #64748b; }
        .disclaimer { text-align: center; font-size: 9.5px; color: #64748b; margin-top: 6px; }
        @media print {
          @page { size: A4 landscape; margin: 6mm 5mm 7mm 5mm; }
          html, body { background: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          .report-sheet { max-width: none !important; margin: 0 !important; padding: 0 !important; }
          .rpt-table th { background: #163a5f !important; color: #fff !important; }
        }
      `}</style>

      <div className="no-print py-5 text-center flex gap-3 justify-center">
        <button onClick={() => navigate(-1)} className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300 rounded" type="button">
          Back
        </button>
        <button onClick={handlePrint} className="bg-[#163a5f] text-white px-6 py-3 text-sm font-medium hover:bg-[#0f2a45] transition-colors rounded" disabled={loading} type="button">
          {loading ? "Loading..." : "Print"}
        </button>
      </div>

      <div className="report-sheet max-w-7xl mx-auto px-4 pb-8 print:max-w-none print:mx-0 print:px-0 print:pb-0">
        <header className="letterhead">
          <div className="letterhead-topbar" />
          <div className="letterhead-body">
            <div className="logo-wrap">
              <img src={companyLogo} alt="INFRACONSOFT" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
            <div className="company-block">
              <h1 className="brand-name">INFRACONSOFT</h1>
              <p className="legal-name">Nissi Infotech &nbsp;|&nbsp; Nissi Civil Construction</p>
              <p className="addr-line">
                3/42-12, Nagappa Colony, Kavundampalayam — 641030, Tamil Nadu, India
                <br />
                Phone: +91 90037 44644 &nbsp;·&nbsp; infraconsoft-implementation@nissiinfotech.in
              </p>
            </div>
            <div className="meta-block">
              <span className="meta-label">Report Date</span>
              <span className="meta-value">{currentDate}</span>
              <span className="meta-label" style={{ marginTop: 7 }}>Financial Year</span>
              <span className="meta-value">{finYear || "—"}</span>
              <span className="meta-label" style={{ marginTop: 7 }}>Document</span>
              <span className="meta-value">JCE / Job Card Print</span>
            </div>
          </div>
          <div className="letterhead-bottombar" />
        </header>

        <div className="doc-title-wrap">
          <h2 className="doc-title">JOB CARD ENTRY DETAIL REPORT</h2>
          <p className="doc-sub">Single job card — workshop maintenance particulars</p>
          <div className="title-rule" />
        </div>

        <section className="id-strip">
          <div className="id-cell">
            <span className="id-k">Entry ID</span>
            <span className="id-v">{jobcardEntryId || "—"}</span>
          </div>
          <div className="id-cell">
            <span className="id-k">Job Card No</span>
            <span className="id-v">{display(row?.jobCardNo)}</span>
          </div>
          <div className="id-cell">
            <span className="id-k">Vehicle Reg No</span>
            <span className="id-v">{display(row?.vehicleRegNo)}</span>
          </div>
          <div className="id-cell">
            <span className="id-k">Status</span>
            <span className="id-v">{display(row?.status)}</span>
          </div>
        </section>

        <table className="rpt-table">
          <thead>
            <tr>
              <th>S. No</th>
              <th>Vehicle Main Type</th>
              <th>Job Card No</th>
              <th>Entry Date</th>
              <th>Vehicle Name</th>
              <th>Vehicle Reg No</th>
              <th>Location</th>
              <th>Hr/Km</th>
              <th>Status</th>
              <th>Nature Of Complaint</th>
              <th>Complaint Attended By</th>
              <th>Inspected By</th>
              <th>Remedy</th>
              <th>From Date</th>
              <th>In Time</th>
              <th>To Date</th>
              <th>Out Time</th>
              <th>Hrs</th>
              <th>Spares Details</th>
              <th>Maintenance By</th>
            </tr>
          </thead>
          <tbody>
            {row ? (
              <tr>
                <td>1</td>
                <td className="left">{display(row.vehicleMainType)}</td>
                <td>{display(row.jobCardNo)}</td>
                <td>{display(row.entryDate)}</td>
                <td className="left">{display(row.vehicleName)}</td>
                <td>{display(row.vehicleRegNo)}</td>
                <td className="left">{display(row.location)}</td>
                <td>{display(row.hrKm)}</td>
                <td>{display(row.status)}</td>
                <td className="left">{display(row.complaint)}</td>
                <td className="left">{display(row.attendedBy)}</td>
                <td className="left">{display(row.inspectedBy)}</td>
                <td className="left">{display(row.remedy)}</td>
                <td>{display(row.fromDate)}</td>
                <td>{display(row.inTime)}</td>
                <td>{display(row.toDate)}</td>
                <td>{display(row.outTime)}</td>
                <td>{display(row.hrs)}</td>
                <td className="left">{display(row.sparesDetails)}</td>
                <td className="left">{display(row.maintenanceBy)}</td>
              </tr>
            ) : (
              <tr>
                <td colSpan={20}>{loading ? "Loading..." : "No record found"}</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="sign-row">
          <div className="gen-by">
            <strong style={{ color: "#163a5f" }}>Prepared by</strong>
            <br />
            Infraconsoft ERP
            <br />
            Automated job card print
            <br />
            Generated: {currentDateTime}
          </div>
          <div className="sign-box">
            <div style={{ height: 36 }} />
            <div className="sign-line">Authorized Signatory</div>
            <div className="sign-hint">Workshop / Fleet Administration</div>
          </div>
        </div>

        <div className="rpt-footer">
          <span>INFRACONSOFT · ERP Software · www.infraconsoft.com · owned by www.nissiinfotech.com</span>
          <span>Confidential — Internal Use Only</span>
        </div>
        <p className="disclaimer">
          This is a computer-generated report. No physical signature is required for authenticity unless mandated by company policy.
        </p>
      </div>
    </div>
  );
}